import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../shared/context/UserContext';
import { getProjectList } from '../../api/disposalApi';
import ScrapProjectStatusBadge from '../../components/disposal/ScrapProjectStatusBadge';
import './DisposalProjectListPage.css';

function fmtDate(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('en-US'); } catch { return v; }
}

export default function DisposalProjectListPage() {
  const { callerEmail } = useUser();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'all' | 'closed'
  const [oemFilter, setOemFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');

  useEffect(() => {
    if (!callerEmail) return;
    setLoading(true);
    setError(null);
    getProjectList('*', callerEmail)
      .then(data => setProjects(data.results || []))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [callerEmail]);

  // Dynamic filter options
  const filterOptions = useMemo(() => ({
    oem: [...new Set(projects.map(p => p.oem).filter(Boolean))].sort(),
    site: [...new Set(projects.map(p => p.site_name || p.site_id).filter(Boolean))].sort(),
  }), [projects]);

  // Filter pipeline
  const visibleProjects = useMemo(() => {
    let result = [...projects];

    // Status
    if (statusFilter === 'active') {
      result = result.filter(p => ['READY', 'IN_PROGRESS'].includes((p.status || '').toUpperCase()));
    } else if (statusFilter === 'closed') {
      result = result.filter(p => (p.status || '').toUpperCase() === 'CLOSED');
    }

    // OEM
    if (oemFilter) result = result.filter(p => p.oem === oemFilter);

    // Site
    if (siteFilter) result = result.filter(p => (p.site_name || p.site_id) === siteFilter);

    // Sort by created_at descending
    result.sort((a, b) => {
      const da = a.created_at || '';
      const db = b.created_at || '';
      return db.localeCompare(da);
    });

    return result;
  }, [projects, statusFilter, oemFilter, siteFilter]);

  function clearFilters() {
    setStatusFilter('active');
    setOemFilter('');
    setSiteFilter('');
  }

  function handleRowClick(project) {
    navigate(`/admin/disposal/${project.project_id}`, {
      state: { site_id: project.site_id },
    });
  }

  return (
    <div className="dpl-page">
      {/* Header */}
      <div className="dpl-header">
        <h1 className="dpl-title">Disposal Projects</h1>
        <div className="dpl-header-spacer" />
        <button className="dpl-new-btn" onClick={() => navigate('/admin/disposal/new')}>
          + New Project
        </button>
      </div>

      {/* Filters */}
      <div className="dpl-filters">
        <div className="dpl-status-group">
          {['active', 'all', 'closed'].map(val => (
            <button
              key={val}
              className={`dpl-status-btn ${statusFilter === val ? 'active' : ''}`}
              onClick={() => setStatusFilter(val)}
            >
              {val.charAt(0).toUpperCase() + val.slice(1)}
            </button>
          ))}
        </div>
        <select className="dpl-filter-select" value={oemFilter} onChange={e => setOemFilter(e.target.value)}>
          <option value="">All OEMs</option>
          {filterOptions.oem.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select className="dpl-filter-select" value={siteFilter} onChange={e => setSiteFilter(e.target.value)}>
          <option value="">All Sites</option>
          {filterOptions.site.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="dpl-empty"><div className="dpl-spinner" /></div>
      ) : error ? (
        <div className="dpl-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
      ) : projects.length === 0 ? (
        <div className="dpl-empty">
          <span>No scrap projects found. Create your first project to get started.</span>
          <button className="dpl-new-btn" onClick={() => navigate('/admin/disposal/new')}>
            + New Project
          </button>
        </div>
      ) : visibleProjects.length === 0 ? (
        <div className="dpl-empty">
          <span>No projects match the current filters.</span>
          <button className="dpl-clear-link" onClick={clearFilters}>Clear Filters</button>
        </div>
      ) : (
        <div className="dpl-table-wrap">
          <table className="dpl-table">
            <thead>
              <tr>
                <th>Project Number</th>
                <th>OEM</th>
                <th>Site</th>
                <th>Hauler</th>
                <th>Status</th>
                <th>Progress</th>
                {/* TODO: uninvoiced BOL count — requires BOL aggregation */}
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {visibleProjects.map(p => {
                const shipped = p.shipped_tool_count || 0;
                const target = p.target_tool_count || 0;
                const pct = target > 0 ? Math.round((shipped / target) * 100) : 0;
                return (
                  <tr key={p.project_id} onClick={() => handleRowClick(p)}>
                    <td><span className="dpl-project-num">{p.project_number}</span></td>
                    <td>{(p.oem || '—').toUpperCase()}</td>
                    <td>{p.site_name || p.site_id || '—'}</td>
                    <td>{p.hauler_name || '—'}</td>
                    <td><ScrapProjectStatusBadge status={p.status} /></td>
                    <td>
                      <div className="dpl-progress-wrap">
                        <div className="dpl-progress-bar">
                          <div className="dpl-progress-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="dpl-progress-text">{shipped} / {target} shipped</span>
                      </div>
                    </td>
                    <td>{fmtDate(p.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* TODO: pagination if list grows */}
        </div>
      )}
    </div>
  );
}
